import { useState } from "react";
import { Calendar, MessageSquare, ExternalLink, Check, Undo2, Loader2, Building2 } from "lucide-react";
import { recordHref } from "../../lib/recordLinks";
import { DueDatePicker } from "../dashboard/todos/DueDatePicker";
import { ClipboardList, Target, Route, TrendingUp, AtSign, ShieldCheck, Crosshair, HeartPulse } from "lucide-react";

/* The company is its own link now, so don't say the name twice. */
const withoutCompany = (item) => {
  const ctx = item.context || "";
  const name = item.link_name;
  if (!name || !recordHref(item.link_type, item.link_id)) return ctx;
  return ctx.startsWith(name) ? ctx.slice(name.length).replace(/^\s*·\s*/, "") : ctx;
};

export const TINTS = {
  action: { fill: "#FFF3CE", ink: "#6B5600", border: "#EADFBE", Icon: ClipboardList },
  ticket: { fill: "#EEF3FA", ink: "#2A4C7A", border: "#C9D9EE", Icon: Target },
  mention: { fill: "#EEF3FA", ink: "#2A4C7A", border: "#C9D9EE", Icon: AtSign },
  journey: { fill: "#EAF6EE", ink: "#1E6B3A", border: "#BEDDC9", Icon: Route },
  goal: { fill: "#EAF6EE", ink: "#1E6B3A", border: "#BEDDC9", Icon: Crosshair },
  deal: { fill: "#FFF6DB", ink: "#7A5E00", border: "#E8D7B4", Icon: TrendingUp },
  quote: { fill: "#FFF6DB", ink: "#7A5E00", border: "#E8D7B4", Icon: TrendingUp },
  meeting: { fill: "#F1EEE6", ink: "#59554D", border: "#DAD5CA", Icon: ClipboardList },
  approval: { fill: "#FDECE5", ink: "#A8320A", border: "#F3C9B7", Icon: ShieldCheck },
  health: { fill: "#FBE3EF", ink: "#B02A6B", border: "#F0C5DC", Icon: HeartPulse },
};

const BTN = "inline-flex items-center justify-center h-8 min-w-8 px-2 rounded-[7px] bg-white border border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A] hover:border-[#C9C3B6] transition-colors";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ddmon = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
};

const relative = (iso, today) => {
  if (!iso) return { text: "No date", late: false, muted: true };
  const days = Math.round((new Date(`${iso}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000);
  if (days < 0) return { text: `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} late`, late: true };
  if (days === 0) return { text: "Due today", late: false };
  return { text: `in ${days} day${days === 1 ? "" : "s"}`, late: false };
};

export const WorkRow = ({ item, today, state, onComplete, onUndo, onDate, onComment, onToggle }) => {
  const tint = TINTS[item.source] || TINTS.action;
  const rel = relative(item.due_date, today);
  const done = !!state.done;
  const [draft, setDraft] = useState(state.draft || "");
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    const ok = await onComment(item, draft.trim());
    setPosting(false);
    if (ok) { setDraft(""); onToggle(item.id, { comment: false, draft: "" }); }
  };

  return (
    <div className="border-t border-[#EFECE4] first:border-t-0" data-testid={`mw-item-${item.id}`}>
      <div className={`grid gap-x-3.5 gap-y-2 px-5 py-3.5 items-start transition-opacity duration-200 grid-cols-[36px_minmax(0,1fr)] md:grid-cols-[36px_minmax(0,1fr)_auto_auto] ${done ? "opacity-50" : ""}`}>
        <div className="w-9 h-9 rounded-[9px] flex items-center justify-center shrink-0"
             style={{ background: tint.fill, border: `1px solid ${tint.border}` }}>
          <tint.Icon className="w-4 h-4" style={{ color: tint.ink }} />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-[5px] whitespace-nowrap"
                  style={{ background: tint.fill, color: tint.ink, border: `1px solid ${tint.border}` }}>{item.tag}</span>
            {item.flag && (
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-[5px] whitespace-nowrap"
                    style={{ background: "#FDECE5", color: "#A8320A" }} data-testid={`mw-flag-${item.id}`}>{item.flag}</span>
            )}
            {item.reference && <span className="mono text-[11px] text-[#8A857C] whitespace-nowrap">{item.reference}</span>}
          </div>
          <div className={`mt-1 text-[14px] font-semibold text-[#1A1A1A] ${done ? "line-through" : ""}`}
               style={{ textWrap: "pretty" }}>{item.title}</div>
          {item.quote && (
            <div className="mt-1 pl-2.5 border-l-2 border-[#DAD5CA] text-[13px] text-[#59554D]">{item.quote}</div>
          )}
          <div className="mt-1 text-[12.5px] text-[#8A857C] flex items-center gap-1.5 flex-wrap">
            {recordHref(item.link_type, item.link_id) && item.link_name && (
              <a href={recordHref(item.link_type, item.link_id)} data-testid={`mw-link-${item.id}`}
                title={`Open ${item.link_name}`}
                className="inline-flex items-center gap-1 font-semibold text-[#1A1A1A] hover:underline">
                <Building2 className="w-3 h-3" />{item.link_name}
              </a>
            )}
            <span>{withoutCompany(item)}</span>
          </div>
          {item.last_comment && !state.comment && (
            <div className="mt-1 text-[12.5px] text-[#59554D]">
              <span className="font-semibold">{item.last_comment.author}:</span> {item.last_comment.text}
            </div>
          )}
        </div>

        <div className="col-start-2 md:col-start-3 text-left md:text-right shrink-0 md:min-w-[92px]">
          <div className="mono text-[12.5px] font-bold whitespace-nowrap"
               style={{ color: rel.late ? "#D24317" : rel.muted ? "#A8A399" : "#1A1A1A" }}>
            {ddmon(item.due_date) || "No date"}
          </div>
          <div className="text-[11.5px] whitespace-nowrap"
               style={{ color: rel.late ? "#D24317" : "#8A857C" }}>{rel.text}</div>
          {item.slip_count > 0 && (
            <div className="mono text-[10px] font-bold mt-0.5 inline-block rounded-[4px] px-1"
              style={{ background: "#FFF4D6", border: "1px solid #E8C86A", color: "#8A6D00" }}
              title={(item.slips || []).map((s) => `${ddmon(s.from)} → ${ddmon(s.to)} · ${s.reason || "no reason"} (${s.by || "?"})`).join("\n")}
              data-testid={`mw-slips-${item.id}`}>moved {item.slip_count}×</div>
          )}
        </div>

        <div className="col-start-2 md:col-start-4 flex items-center justify-end gap-1.5 shrink-0 flex-wrap">
          {item.completion && (
            done ? (
              <button className={`${BTN} gap-1.5 text-[12.5px] font-semibold`} onClick={() => onUndo(item)}
                      data-testid={`mw-undo-${item.id}`}><Undo2 className="w-3.5 h-3.5" /> Undo</button>
            ) : (
              <button className={`${BTN} gap-1.5 text-[12.5px] font-semibold`} onClick={() => onComplete(item)}
                      data-testid={`mw-complete-${item.id}`}>
                {state.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" style={{ color: "#1E6B3A" }} />}
                {item.completion}
              </button>
            )
          )}
          {item.can_date && (
            <DueDatePicker id={item.id} value={item.due_date || null}
                           onPick={(iso) => onDate(item, iso)}>
              <button className={BTN} title="Change the date" data-testid={`mw-date-${item.id}`}>
                <Calendar className="w-3.5 h-3.5" />
              </button>
            </DueDatePicker>
          )}
          {item.can_comment && (
            <button className={`${BTN} gap-1`} title="Comment" data-testid={`mw-comment-${item.id}`}
                    onClick={() => onToggle(item.id, { comment: !state.comment }, true)}>
              <MessageSquare className="w-3.5 h-3.5" />
              {item.comments_count > 0 && <span className="mono text-[11px]">{item.comments_count}</span>}
            </button>
          )}
          <a className={BTN} title={item.open_label} data-testid={`mw-open-${item.id}`}
             href={item.open_path} target="equipr-app" rel="noopener">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {state.comment && (
        <div className="px-5 pb-4 pl-[68px]" data-testid={`mw-comment-box-${item.id}`}>
          <textarea value={draft} autoFocus rows={2}
                    onChange={(e) => { setDraft(e.target.value); onToggle(item.id, { draft: e.target.value }); }}
                    placeholder="Add a comment. It posts to the source record."
                    className="w-full text-[13px] p-2.5 border border-[#DAD5CA] rounded-[7px] bg-white resize-y focus:outline-none focus:border-[#C9C3B6]" />
          <div className="mt-2 flex items-center gap-2">
            <button onClick={post} disabled={!draft.trim() || posting} data-testid={`mw-post-${item.id}`}
                    className="h-8 px-3 rounded-[7px] text-[12.5px] font-semibold disabled:bg-[#E6E2D9] disabled:text-[#B5AFA3]"
                    style={draft.trim() && !posting ? { background: "#FFD108", color: "#121212" } : undefined}>
              {posting ? "Posting…" : "Post"}
            </button>
            <button onClick={() => onToggle(item.id, { comment: false })}
                    className="text-[12.5px] text-[#59554D] hover:text-[#1A1A1A]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
